// @givmo/employer-module — sign-up OTP screen (/sign-up/otp). Ports `sign_up_otp_screen.dart`.
//
// "Verify your work email" + the 6-digit OtpField + "Resend code" (≈60s cooldown) + "Verify" + the
// host's sign-out. The OTP SEND fires once on mount. FOUR arrival modes:
//   - `?register=true` — the EMAIL sign-up (NF-FIX21): `adminSignUpWithEmail` created the account and
//     its Givmo row and NO company. The work email is proven HERE, BEFORE any company exists, and
//     post-confirm the screen registers the company from the details the first screen carried
//     (`sign-up-context.tsx`, through `use-register-company.ts`): created → /sign-up/verified, the
//     pending room, as before; the join pivot → /sign-up/request-access, already verified. A refused
//     registration (a duplicate name, a domain the create refuses) goes back to the details screen,
//     signed in and prefilled, with the server's message — the account is verified and kept. So does
//     an arrival whose carried details a reload lost. Until NF-FIX21 the email path registered first
//     and verified here after, so a sign-up abandoned at this screen still held the company's domain;
//   - the DEFAULT — an account that already HAS a company and still owes its code (the
//     `emailUnverified` routing state; since NF-FIX21 the details screen's registration no longer
//     sends here, because only a verified account reaches its create) — post-confirm goes to
//     /sign-up/verified, the registered-and-pending room;
//   - `?provider=true` — an account with NO company whose email the backend OTP must repair: a
//     microsoft.com user (GCIP mints those unverified; 2026-08-18), an email account that stopped at
//     this screen and signed in again, or (NF-FIX21) any other unverified account — an APP account,
//     with a phone linked, landed here or sent by the details screen with what it typed carried over.
//     Post-confirm RE-RESOLVES the routing state
//     instead of assuming the pending room: a companyless user goes onward to the details screen; one
//     with a company lands wherever its status says;
//   - `?join=true` — a join-or-create requester (EP-SUITE2-FIX2) with no company and an unverified
//     work email, which the join-request intake refuses. They verify HERE, and post-confirm the screen
//     lands on /sign-up/request-access (the join store carries the company, or the screen re-resolves
//     it) — never the pending room, whose "a member of our team will reach out" copy would be false
//     for someone who must request access.
// The mount send needs a backend user row (the OTP endpoints 403 without one): the email sign-up
// provisions it when it creates the account, and every other arrival is covered by `sendOtp`, which
// bootstraps the row itself (the host's auth context, over `auth/ensure-backend-user.ts`). A wrong
// code shows "Incorrect code. Please try again."
//
// Rendered by both hosts (EP-MNT8): the three arrival marks arrive as props (each host reads its own
// route's search), and the auth, addresses, look and frame come from `useSignUpHost()`.
//
// And a FIFTH arrival, a page rather than a route (EP-MNT17): the company console's invite page draws
// this screen in place for an account it just made, and hands `onVerified`. Then nothing here
// navigates after the code: the page redeems its invite itself, on its own address.

import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Button, OtpField, ErrorMessage, testIdProp } from '@givmo/design-system';
import { AuthError } from '@givmo/auth';
import { SignUpHeading, useSignUpHost } from './sign-up-host';
import { useAuthError } from './use-auth-error';
import { useSignUpDetails } from './sign-up-context';
import { useRegisterCompany } from './use-register-company';
import { navigationForRoutingState } from './auth-routing';

const RESEND_COOLDOWN_SECONDS = 60;
const WRONG_CODE_MESSAGE = 'Incorrect code. Please try again.';

export interface SignUpOtpScreenProps {
  /** `?provider=true` — re-resolve after the code (see the header). Wins over the other two. */
  provider?: boolean;
  /** `?join=true` — a join-or-create requester: request-access after the code. */
  join?: boolean;
  /** `?register=true` — the email sign-up: register the carried company after the code. */
  register?: boolean;
  /**
   * The invite page's own arrival (EP-MNT17): called once the code is confirmed, INSTEAD of every
   * mode's navigation above — the page that drew this screen goes on itself. Wins over the marks.
   */
  onVerified?: () => void | Promise<void>;
}

export function SignUpOtpScreen({ provider, join, register, onVerified }: SignUpOtpScreenProps) {
  const { auth, paths, look, words, Shell } = useSignUpHost();
  const { user, sendOtp, confirmOtp, signOut, refreshRoutingState } = auth;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { details, setRegistrationError } = useSignUpDetails(user?.uid ?? null);
  const registerCompany = useRegisterCompany();
  const { error, clearError, surface } = useAuthError();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [wrongCode, setWrongCode] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Fire the verification email once on mount (sendOtp bootstraps the backend user row for the
  // provider-first arrival; the email flow's registration already created it).
  const sentRef = useRef(false);
  useEffect(() => {
    if (sentRef.current) return;
    sentRef.current = true;
    void sendOtp().catch((thrown) => surface(thrown));
  }, [sendOtp, surface]);

  // Resend cooldown ticker.
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const canResend = cooldown <= 0;

  const onResend = async () => {
    if (!canResend) return;
    clearError();
    setWrongCode(false);
    setCooldown(RESEND_COOLDOWN_SECONDS);
    try {
      await sendOtp();
    } catch (thrown) {
      surface(thrown);
    }
  };

  const onVerify = async () => {
    if (code.length !== 6) return;
    clearError();
    setWrongCode(false);
    setSubmitting(true);
    try {
      await confirmOtp(code);
      if (onVerified !== undefined) {
        // The invite page's arrival: the code IS confirmed, and the page redeems on its own address.
        await onVerified();
      } else if (provider) {
        // Provider-first arrival: the verified flag just flipped, so decide the next screen fresh —
        // a companyless caller resolves `unregistered` (→ details, provider path); one with a
        // company lands wherever its status says. Never the email flow's pending-room assumption.
        // The code IS confirmed by here: a resolve failure must not read as "incorrect code", so it
        // falls back to the details screen (the resolve re-runs there) rather than into the catch.
        const state = await refreshRoutingState({ isProviderRegistration: true }).catch(() => null);
        await navigate(navigationForRoutingState(state ?? 'unregistered', undefined, {}, paths));
      } else if (join) {
        // A join-or-create requester: the work email is verified now, so the request-access screen
        // may file the join request (it re-checks the credential itself and sends the request under
        // a freshly minted token — EP-SUITE2-FIX2). The join store carries the company, or that
        // screen resolves it again for the now-verified caller (EP-SUITE2-FIX3).
        await navigate({ to: paths.signUpRequestAccess });
      } else if (register) {
        // NF-FIX21: the work email is proven, so the company may now be registered.
        await finishEmailRegistration();
      } else {
        await navigate({ to: paths.signUpVerified });
      }
    } catch (thrown) {
      setWrongCode(true);
      // Surface only for non-cancellation; the inline wrong-code message is the primary signal.
      surface(thrown);
    } finally {
      setSubmitting(false);
    }
  };

  // NF-FIX21 — the email sign-up's registration, after its code. It never throws: the code IS
  // confirmed by here, so nothing that follows may read as "incorrect code". Every exit that leaves on
  // its own for the details screen REPLACES this entry — re-entered with Back, this screen would mail
  // another code to an address already proven.
  const finishEmailRegistration = async () => {
    if (details === null) {
      // A reload between the screens lost what the first screen collected. The account is verified,
      // so the details screen's registration for an existing account takes it from here.
      await navigate({ to: paths.signUpDetails, search: { provider: true }, replace: true });
      return;
    }
    try {
      const outcome = await registerCompany(details);
      if (outcome === 'created') await navigate({ to: paths.signUpVerified });
    } catch (thrown) {
      // A refused registration — a duplicate company name, or a domain the create refuses. The account
      // is verified and KEPT (it is real now; a rollback would only make the person verify again): the
      // details screen shows the server's own message over what they typed and registers again.
      const refused = AuthError.from(thrown);
      setRegistrationError(
        refused.message.length > 0 ? refused.message : AuthError.userMessage('unknown'),
      );
      await navigate({ to: paths.signUpDetails, search: { provider: true }, replace: true });
    }
  };

  // EP-SUITE2-FIX5: this screen is where every door LANDS an unverified account with no company, and
  // Logout is its only way out — a bare sign-out left a signed-out visitor on "Verify your work email".
  // It leaves for the host's sign-in door, the shell's way: cache cleared first, because React commits
  // the sign-out a render after `signOut()` resolves and the door's arrival guard still sees this
  // account (`app.sign-out-door.test.tsx`); replacing, so Back does not return to a code for an
  // account gone.
  const onLogout = async () => {
    await signOut();
    queryClient.clear();
    await navigate({ to: paths.loginHome, replace: true });
  };

  return (
    <Shell testId="page-sign-up-otp">
      <SignUpHeading heading={<h1 className={look.heading}>Verify your work email</h1>}>
        <p className={look.body}>
          Please enter the six-digit code that was sent to your email address.
        </p>
      </SignUpHeading>

      <OtpField
        length={6}
        value={code}
        onValueChange={setCode}
        error={wrongCode}
        aria-label="Verification code"
        testId="auth-otp"
      />
      {wrongCode && <ErrorMessage testId="auth-otp-error">{WRONG_CODE_MESSAGE}</ErrorMessage>}
      {error !== null && !wrongCode && <ErrorMessage testId="auth-error">{error}</ErrorMessage>}

      {/* Verify first, its two text controls held under it, as the sign-in doors put their primary
          action before their links. */}
      <Button
        {...look.primary}
        loading={submitting}
        disabled={code.length !== 6 || submitting}
        onClick={() => void onVerify()}
        testId="auth-verify"
      >
        Verify
      </Button>

      <div className="flex flex-col gap-8">
        <button
          type="button"
          onClick={() => void onResend()}
          disabled={!canResend}
          className={look.resendButton(canResend)}
          {...testIdProp('auth-resend')}
        >
          {canResend ? 'Resend code' : `Resend code (${cooldown}s)`}
        </button>

        <button
          type="button"
          onClick={() => void onLogout()}
          className={look.textButton}
          {...testIdProp('auth-logout')}
        >
          {words.signOut}
        </button>
      </div>
    </Shell>
  );
}
